import React, { useEffect, useRef, useState } from 'react';

import type {
  YieldShooterInstance,
  YieldShooterEventParams,
  YieldShooterState,
} from '../types/yieldShooter';

export type GameResult = {
  status: 'levelPassed' | 'levelFailed' | 'gameCompleted' | 'gameFailed';
  level: number;
  score: number;
};

type Props = {
  /** Fired on each level pass/fail and on game completion. */
  onResult?: (result: GameResult) => void;
  /** Fired when the game's own "Quit" button is pressed. */
  onExit?: () => void;
};

type LoadState = 'loading' | 'ready' | 'error';

/* Absolute URL to the vendored bundle. It is a `.js`, which Amplify's SPA
   rewrite serves as-is — unlike a `.html` host page, which gets rewritten to
   index.html. So we DON'T point the iframe at a play.html file; we inline the
   host via `srcDoc` below and load the bundle by absolute URL. */
const APP_JS = `${process.env.PUBLIC_URL || ''}/puzzles/yield-shooter/app.js`;

/* Inlined host document for the iframe. A srcdoc frame is same-origin with the
   parent, so React can mount the game and tap its hooks directly (no
   postMessage). The script src MUST be absolute — srcdoc resolves relative URLs
   against the PARENT page, not this document. Dark fill avoids a white flash and
   covers the game's (http, mixed-content-blocked) decorative background. */
const HOST_DOC =
  '<!doctype html><html><head><meta charset="utf-8">' +
  '<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;background:#0c1f3c}' +
  '#container{position:absolute;inset:0}</style>' +
  `<script src="${APP_JS}"></` +
  'script></head><body><div id="container"></div></body></html>';

const RESULT_EVENTS = new Set([
  'levelPassed',
  'levelFailed',
  'gameCompleted',
  'gameFailed',
]);

/**
 * Hosts the vendored Yield Shooter inside a same-origin `srcDoc` iframe and
 * drives it from React. The iframe gives the game its own document + window, so
 * its GLOBAL injected styles (`svg {…}`, `@font-face`) and `window`-based sizing
 * stay contained instead of clobbering pm.web. Using `srcDoc` (rather than an
 * iframe `src` to a .html file) makes it deployment-proof: there is no `.html`
 * asset for a host's SPA rewrite (e.g. AWS Amplify) to hijack into index.html.
 */
const YieldShooterGame: React.FC<Props> = ({ onResult, onExit }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const instanceRef = useRef<YieldShooterInstance | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  // Keep latest callbacks without rebinding the iframe.
  const onResultRef = useRef(onResult);
  const onExitRef = useRef(onExit);
  onResultRef.current = onResult;
  onExitRef.current = onExit;

  const handleLoad = () => {
    const frame = iframeRef.current;
    const win = frame?.contentWindow;
    const container = frame?.contentDocument?.getElementById('container');
    if (!win || !container) {
      setLoadState('error');
      return;
    }

    // The bundle is a synchronous <script> in the srcdoc <head>, so the global
    // is normally present at the load event — poll briefly for the 5MB parse.
    let tries = 0;
    const start = () => {
      if (!win.startYieldShooter) {
        if (tries++ > 60) setLoadState('error');
        else window.setTimeout(start, 50);
        return;
      }

      const instance = win.startYieldShooter({
        config: {
          game: {},
          lang: {
            defaultLang: 'en',
            availableDefinitions: [
              { code: 'en', name: 'English' },
              { code: 'ro', name: 'Română' },
            ],
          },
        },
        domContainer: container,
      });
      instanceRef.current = instance;
      setLoadState('ready');

      instance.hooks.gameEvent.tap(
        'pmweb',
        (_name: string, params: YieldShooterEventParams, state: YieldShooterState) => {
          const name = params?.name;
          if (name && RESULT_EVENTS.has(name)) {
            onResultRef.current?.({
              status: name as GameResult['status'],
              level: Number(state?.levelNumber ?? params?.level ?? 0),
              score: Number(state?.score ?? params?.score ?? 0),
            });
          }
        }
      );
      instance.hooks.requestExitGame.tap('pmweb', () => onExitRef.current?.());
    };

    start();
  };

  // Dispose runs before React removes the <iframe> (the iframe teardown then
  // disposes of everything else: document, styles, hotkeys).
  useEffect(
    () => () => {
      try {
        instanceRef.current?.dispose();
      } catch {
        /* iframe removal handles the rest */
      }
      instanceRef.current = null;
    },
    []
  );

  return (
    <div className="puzzles__game">
      {loadState === 'loading' && (
        <div className="puzzles__game-status">Loading game…</div>
      )}
      {loadState === 'error' && (
        <div className="puzzles__game-status">
          Couldn’t load the game bundle (<code>/puzzles/yield-shooter/app.js</code>).
        </div>
      )}
      <iframe
        ref={iframeRef}
        className="puzzles__game-frame"
        title="Yield Shooter"
        srcDoc={HOST_DOC}
        onLoad={handleLoad}
      />
    </div>
  );
};

export default YieldShooterGame;
